/** @jsx isolateComponent */

import { useLayoutEffect, useRef, useState, useEffect } from 'react';
import { isolateComponent } from '@zapier/style-encapsulation';

import { Nav } from './Nav';
import type { Props as NavProps, NavRefElement } from './Nav';
import type { NavItemType } from './types';

import { debounce } from '../../../utils/debounce';

type Props = NavProps;

/**
 * Creates a `Nav` that automatically places items within its dropdown
 * as space limitations necessitate.
 */
export const AutoNav = (props: Props) => {
  const navRef = useRef<NavRefElement>(null);
  const [items, setItems] = useState(props.items);
  // Detect whether the navigation is overflowing, and if it is then
  // place items into its dropdown until it is no longer overflowing.
  useLayoutEffect(() => {
    const nav = navRef.current;
    const isOverflowing = !!nav && nav.scrollWidth > nav.offsetWidth;
    if (isOverflowing) {
      // Find the index of the first item that's `isExtra: true`...
      const hasExtraItems = items.some((item) => item.isExtra);
      const indexOfFirstExtraItem = hasExtraItems
        ? items.findIndex((item: NavItemType) => item.isExtra)
        : items.length;
      // ...and then set the item _before_ it to be `isExtra: true`,
      // thereby making _it_ the first item that is `isExtra: true`.
      setItems(
        items.map((item, i) => {
          return i < indexOfFirstExtraItem - 1
            ? item
            : {
                ...item,
                isExtra: true,
              };
        })
      );
    }
  }, [items]);
  // Whenever the window resizes, reset `items` to a new array
  // to effectively force a rerender in order to recalculate which
  // items should move to the dropdown. This isn't a perfect solution
  // since it relies on window resizing, but until we have a use case
  // that breaks this, it should be sufficient.
  useLayoutEffect(() => {
    const onResize = debounce(() => {
      // Note that `props.items` is used over `items` to reset all
      // changed `isExtra` values to their original values. Without
      // this,  any items that were changed to `isExtra: true` would
      // not revert to `isExtra: false` even if there was space
      // available for them to do so.
      setItems([...props.items]);
    }, 100);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  });
  useEffect(() => setItems([...props.items]), [props.items]);

  return (
    <Nav
      {...props}
      forwardedRef={navRef}
      items={items}
      selectedItemHref={props.selectedItemHref}
    />
  );
};

AutoNav.defaultProps = {
  shouldRenderTrack: true,
  onChooseItem: () => {},
};
